import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { logger } from "@/utils/logger";
import { useToast } from "@/hooks/use-toast";
import { hapticSuccess, hapticError } from "@/utils/haptics";
import { generateUniqueSlug, generateUniqueCompanySlug } from "@/utils/slugValidation";
import { generatePasscode } from "@/utils/passcodeUtils";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog-mobile-optimized";
import { Progress } from "@/components/ui/progress";
import { WelcomeStep } from "./steps/WelcomeStep";
import { PagePreviewStep } from "./steps/PagePreviewStep";
import { BrandingStep } from "./steps/BrandingStep";
import { ShareStep } from "./steps/ShareStep";
import { CampaignStep } from "./steps/CampaignStep";
import { SuccessStep } from "./steps/SuccessStep";

interface OnboardingWizardProps {
  open: boolean;
  onComplete: () => void;
}

export type OnboardingStep = 'welcome' | 'preview' | 'branding' | 'share' | 'campaign' | 'success';

interface CreatedPage {
  id: string;
  title: string;
  description: string;
  slug: string;
  passcode: string | null;
}

export const OnboardingWizard = ({ open, onComplete }: OnboardingWizardProps) => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const [companyName, setCompanyName] = useState("");
  const [createdPage, setCreatedPage] = useState<CreatedPage | null>(null);
  const [brandColor, setBrandColor] = useState("#5B72FF");
  const [logoUrl, setLogoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const stepOrder: OnboardingStep[] = ['welcome', 'preview', 'branding', 'share', 'campaign', 'success'];
  const currentIndex = stepOrder.indexOf(currentStep);
  const progress = ((currentIndex) / (stepOrder.length - 1)) * 100;

  const handleCompanySubmit = async (name: string) => {
    setLoading(true);
    setError("");

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not found");

      // Generate unique company slug
      const companySlug = await generateUniqueCompanySlug(name.trim());

      // Create company and owner membership atomically
      const { data: companyId, error: companyError } = await supabase
        .rpc('create_company_with_owner', {
          p_name: name.trim(),
          p_slug: companySlug,
        });

      if (companyError) throw companyError;

      // Update profile with company name
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ company_name: name.trim() })
        .eq("id", user.id);

      if (updateError) throw updateError;

      // Create feedback page
      const pageSlug = await generateUniqueSlug(`${name.trim()}-feedback`, user.id);
      const pageTitle = `${name.trim()} Team Feedback`;
      const pageDescription = `Share your honest thoughts about team dynamics, collaboration, and workplace culture. Your feedback helps us build a better environment.`;

      const { data: pageData, error: pageError } = await supabase
        .from("feedback_pages")
        .insert({
          user_id: user.id,
          company_id: companyId,
          title: pageTitle,
          description: pageDescription,
          slug: pageSlug,
          passcode: generatePasscode(), // Auto-generate passcode
          is_active: true,
        })
        .select()
        .single();

      if (pageError) throw pageError;

      // Invalidate feedback pages cache to ensure UI shows the new page as active
      queryClient.invalidateQueries({ queryKey: ['feedback-pages'] });

      // Create notification preferences
      await supabase
        .from("notification_preferences")
        .insert({
          user_id: user.id,
          new_feedback_enabled: true,
          daily_digest_enabled: false,
          weekly_digest_enabled: true,
          sentiment_alerts_enabled: true,
        });

      setCompanyName(name.trim());
      setCreatedPage({
        id: pageData.id,
        title: pageData.title,
        description: pageData.description,
        slug: pageData.slug,
        passcode: pageData.passcode,
      });

      hapticSuccess();
      setCurrentStep('preview');
    } catch (err: any) {
      logger.error('Onboarding setup failed', err);
      hapticError();
      setError(err.message);
      toast({
        title: "Setup failed",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBrandingSave = async (color: string, logo: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Update profile branding
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ 
          brand_color: color,
          logo_url: logo || null,
        })
        .eq("id", user.id);

      if (profileError) throw profileError;

      // Also update company branding if company exists
      if (createdPage) {
        const { data: page } = await supabase
          .from("feedback_pages")
          .select("company_id")
          .eq("id", createdPage.id)
          .single();

        if (page?.company_id) {
          await supabase
            .from("companies")
            .update({ 
              brand_color: color,
              logo_url: logo || null,
            })
            .eq("id", page.company_id);
        }
      }

      setBrandColor(color);
      setLogoUrl(logo);
      hapticSuccess();
      setCurrentStep('share');
    } catch (err: any) {
      logger.error('Failed to save branding', err);
      toast({
        title: "Failed to save branding",
        description: "You can update this later in settings.",
        variant: "destructive",
      });
      setCurrentStep('share');
    }
  };

  const handleComplete = () => {
    // Clear tour flags so TourPrompt shows for new users after onboarding
    localStorage.removeItem("tourPromptDismissed");
    localStorage.removeItem("tourCompleted");
    onComplete();
  };

  const goToStep = (step: OnboardingStep) => {
    setCurrentStep(step);
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Progress bar */}
        {currentStep !== 'welcome' && currentStep !== 'success' && (
          <div className="mb-6">
            <div className="flex justify-between text-xs text-muted-foreground mb-2">
              <span>Step {currentIndex} of {stepOrder.length - 2}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {/* Step content */}
        {currentStep === 'welcome' && (
          <WelcomeStep
            onSubmit={handleCompanySubmit}
            loading={loading}
            error={error}
          />
        )}

        {currentStep === 'preview' && createdPage && (
          <PagePreviewStep
            page={createdPage}
            companyName={companyName}
            onNext={() => setCurrentStep('branding')}
          />
        )}

        {currentStep === 'branding' && createdPage && (
          <BrandingStep
            pageTitle={createdPage.title}
            initialColor={brandColor}
            initialLogo={logoUrl}
            onSave={handleBrandingSave}
            onSkip={() => setCurrentStep('share')}
          />
        )}

        {currentStep === 'share' && createdPage && (
          <ShareStep
            pageSlug={createdPage.slug}
            companyName={companyName}
            onNext={() => setCurrentStep('campaign')}
            onSkip={() => setCurrentStep('success')}
          />
        )}

        {currentStep === 'campaign' && createdPage && (
          <CampaignStep
            pageId={createdPage.id}
            pageTitle={createdPage.title}
            companyName={companyName}
            onNext={() => setCurrentStep('success')}
            onSkip={() => setCurrentStep('success')}
          />
        )}

        {currentStep === 'success' && createdPage && (
          <SuccessStep
            pageSlug={createdPage.slug}
            companyName={companyName}
            onComplete={handleComplete}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
